// Existing-project onboarding contracts (spec `docs/superpowers/specs/2026-09-28-existing-project-onboarding-design.md`,
// decision D26): the codebase-analysis vocabulary (verdicts, sources, flags, run and provisioning states), the
// excerpt/evidence bounds, the `codebase_analysis` section of the routing configuration (off by default), the API DTOs
// of analysis runs, review and closures, the GitHub onboarding request/provisioning shapes, and the Jev evidence port.
// Stored evidence is references only ({path,start,end}); code text is never persisted (C10).
import { z } from "zod";
import { TASK_STATUSES } from "./enums.ts";

const id = z.uuid();
const timestamp = z.iso.datetime();
const taskStatus = z.enum(TASK_STATUSES as unknown as [string, ...string[]]);

// ---------- Vocabulary ----------

export const CODE_VERDICTS = ["implemented", "partial", "not_found", "uncertain"] as const;
export type CodeVerdict = (typeof CODE_VERDICTS)[number];

/** Who decided a verdict: the deterministic trace, Jev, or a human answering a review item. */
export const VERDICT_SOURCES = ["trace", "jev", "human"] as const;
export type VerdictSource = (typeof VERDICT_SOURCES)[number];

export const VERDICT_FLAGS = ["prioritized", "budget_stop", "jev_failed", "reused", "docs_only"] as const;
export type VerdictFlag = (typeof VERDICT_FLAGS)[number];

/** How a task reached `done`: built by the pipeline, closed as already in the code, or an admin override. */
export const COMPLETION_KINDS = ["built", "existing", "override"] as const;
export type CompletionKind = (typeof COMPLETION_KINDS)[number];

export const ANALYSIS_TRIGGERS = ["import", "manual", "onboarding"] as const;
export type AnalysisTrigger = (typeof ANALYSIS_TRIGGERS)[number];

export const ANALYSIS_STATES = ["queued", "running", "done", "failed", "cancelled"] as const;
export type AnalysisState = (typeof ANALYSIS_STATES)[number];

export const PROVISION_STATES = ["queued", "cloning", "detecting", "validating", "done", "failed"] as const;
export type ProvisionState = (typeof PROVISION_STATES)[number];

export const PROVISION_ERRORS = [
  "too_large",
  "disk_low",
  "timeout",
  "host_not_allowed",
  "auth_failed",
  "not_found",
  "base_branch_missing",
  "clone_failed",
  "connection_unavailable",
  "not_git_root",
] as const;
export type ProvisionError = (typeof PROVISION_ERRORS)[number];

// ---------- Bounds ----------

/** Excerpt sent to Jev per requirement: characters, context lines around a hit, characters per line, keywords searched. */
export const EXCERPT_MAX_CHARS = 6000;
export const EXCERPT_CONTEXT_LINES = 15;
export const EXCERPT_MAX_LINE_CHARS = 400;
export const MAX_SEARCH_KEYWORDS = 6;
/** Requirements judged per activity batch. */
export const ANALYSIS_BATCH_SIZE = 10;
/** Output of the Jev model is free (see `JEV_INPUT_USD_PER_MTOK`), so only input tokens are priced. */
export const JEV_OUTPUT_USD_PER_MTOK = 0;
/** Tokens reserved for the answer when a call is estimated. */
export const JEV_ANSWER_TOKEN_ALLOWANCE = 64;
/** Version of the text the organization admin accepts before the first analysis (audit trail of the consent). */
export const ATTESTATION_TEXT_VERSION = "codebase-analysis-attestation-1" as const;

// ---------- Evidence: references only ----------

/** Stored evidence bounds: references per verdict, characters per path, and the 0017 CHECK on the jsonb text (bytes). */
export const EVIDENCE_MAX_REFS = 20;
export const EVIDENCE_PATH_MAX_CHARS = 300;
export const EVIDENCE_MAX_BYTES = 8192;

export const evidenceRefSchema = z
  .object({
    path: z.string().min(1).max(EVIDENCE_PATH_MAX_CHARS),
    start: z.number().int().min(1),
    end: z.number().int().min(1),
  })
  .strict()
  .refine((r) => r.end >= r.start, { message: "end must not precede start", path: ["end"] });
export type EvidenceRef = z.infer<typeof evidenceRefSchema>;

/** ≤ 20 references and at most one of each flag: 8 KB at the maximum (asserted by the contract test). */
export const verdictEvidenceSchema = z
  .object({
    refs: z.array(evidenceRefSchema).max(EVIDENCE_MAX_REFS),
    truncated: z.boolean(),
    flags: z.array(z.enum(VERDICT_FLAGS)).max(VERDICT_FLAGS.length),
  })
  .strict();
export type VerdictEvidence = z.infer<typeof verdictEvidenceSchema>;

// ---------- Configuration (routing config `codebase_analysis` section) ----------

const analysisFields = {
  enabled: z.boolean(),
  max_cost_cents_per_run: z.number().min(0).max(10_000),
  auto_close: z.boolean(),
};

/** Effective section: off by default, a 50 cent ceiling per run, closures applied automatically (7c: the first run turns `auto_close` off). */
export const codebaseAnalysisConfigSchema = z
  .object({
    enabled: analysisFields.enabled.default(false),
    max_cost_cents_per_run: analysisFields.max_cost_cents_per_run.default(50),
    auto_close: analysisFields.auto_close.default(true),
  })
  .strict();
export type CodebaseAnalysisConfig = z.infer<typeof codebaseAnalysisConfigSchema>;
export const DEFAULT_CODEBASE_ANALYSIS: CodebaseAnalysisConfig = codebaseAnalysisConfigSchema.parse({});

/**
 * The `codebase_analysis` section of one routing LAYER: no defaults — they apply only at resolution (domain
 * STATIC_FIELDS). A default here would pin every project layer to `enabled: false` (the `and` merge switches off) and
 * to the 50 cent ceiling (the `min` merge), whatever the organization granted (same rule as `assistantConfigLayerSchema`).
 */
export const codebaseAnalysisConfigLayerSchema = z.object(analysisFields).partial().strict();

// ---------- Verdicts, runs, review ----------

export const codeVerdictSchema = z.object({
  run_id: id,
  requirement_id: id,
  verdict: z.enum(CODE_VERDICTS),
  source: z.enum(VERDICT_SOURCES),
  /** The only evidence is test/fixture code: never closes a task on its own. */
  fixture_only: z.boolean(),
  evidence: verdictEvidenceSchema,
  cost_usd: z.number().nullable(),
  decided_by: id.nullable(),
  created_at: timestamp,
});
export type CodeVerdictRow = z.infer<typeof codeVerdictSchema>;

const count = z.number().int().nonnegative();

export const codebaseAnalysisRunSchema = z.object({
  id,
  project_id: id,
  prd_version_id: id,
  trigger: z.enum(ANALYSIS_TRIGGERS),
  state: z.enum(ANALYSIS_STATES),
  base_commit: z.string().nullable(),
  auto_close: z.boolean().nullable(),
  counts: z.object({ implemented: count, partial: count, not_found: count, uncertain: count, closed: count }),
  cost_usd: z.number().nullable(),
  error: z.string().nullable(),
  created_at: timestamp,
  started_at: timestamp.nullable(),
  finished_at: timestamp.nullable(),
});
export type CodebaseAnalysisRun = z.infer<typeof codebaseAnalysisRunSchema>;

/** A requirement a human must look at: Jev was unsure, or Jev says implemented but auto-close is off or refused. */
export const reviewItemSchema = z.object({
  requirement: z.object({ id, external_id: z.string(), title: z.string() }),
  reason: z.enum(["uncertain", "would_close"]),
  verdict: codeVerdictSchema,
  open_tasks: z.array(z.object({ id, number: z.number().int(), title: z.string(), status: taskStatus })),
});
export type ReviewItem = z.infer<typeof reviewItemSchema>;

export const closedItemSchema = z.object({
  task: z.object({ id, number: z.number().int(), title: z.string(), status: taskStatus, version: z.number().int() }),
  requirement_external_id: z.string(),
  fixture_only: z.boolean(),
  refs: z.array(evidenceRefSchema),
  /** Still `done` as `existing` at the version the closure left: only then can it be reopened. */
  reopenable: z.boolean(),
});
export type ClosedItem = z.infer<typeof closedItemSchema>;

export const codebaseAnalysisDetailSchema = z.object({
  run: codebaseAnalysisRunSchema,
  review: z.array(reviewItemSchema),
  closed: z.array(closedItemSchema),
});
export type CodebaseAnalysisDetail = z.infer<typeof codebaseAnalysisDetailSchema>;

// ---------- Requests ----------

export const startAnalysisRequestSchema = z.object({ prd_version_id: id });
export type StartAnalysisRequest = z.infer<typeof startAnalysisRequestSchema>;

export const analysisAnswerRequestSchema = z.object({ requirement_id: id, answer: z.enum(["ready", "not_ready"]) });
export type AnalysisAnswerRequest = z.infer<typeof analysisAnswerRequestSchema>;

export const attestRequestSchema = z.object({ accept: z.literal(true), text_version: z.literal(ATTESTATION_TEXT_VERSION) });
export type AttestRequest = z.infer<typeof attestRequestSchema>;

export const attestationSchema = z.object({
  attested: z.boolean(),
  by: id.nullable(),
  at: timestamp.nullable(),
  text_version: z.string().nullable(),
});
export type Attestation = z.infer<typeof attestationSchema>;

// ---------- GitHub onboarding ----------

export const fromGithubRequestSchema = z.object({ integration_id: id, repository: z.string().regex(/^[A-Za-z0-9_.-]{1,100}\/[A-Za-z0-9_.-]{1,100}$/) });
export type FromGithubRequest = z.infer<typeof fromGithubRequestSchema>;

export const repositoryProvisionSchema = z.object({
  id,
  project_id: id,
  integration_id: id,
  repository: z.string(),
  state: z.enum(PROVISION_STATES),
  error_code: z.enum(PROVISION_ERRORS).nullable(),
  error: z.string().nullable(),
  commit: z.string().nullable(),
  test_command: z.string().nullable(),
  size_kb: z.number().nullable(),
  created_at: timestamp,
  started_at: timestamp.nullable(),
  finished_at: timestamp.nullable(),
});
export type RepositoryProvision = z.infer<typeof repositoryProvisionSchema>;

// ---------- Jev evidence port ----------

/** What MaestrOS sends to Jev per requirement: text and criteria plus redacted excerpts, never secret files (C9, C10). */
export interface JevEvidenceInput {
  requirement: { code: string; title: string; criteria: string[] };
  excerpts: { path: string; text: string }[];
  /** Hash of the exact input: the reuse key of a verdict on the same commit. */
  inputHash: string;
}

export interface JevEvidenceAnswer {
  verdict: CodeVerdict;
  confidence: number;
  model: string;
  /** Provider-reported cost; null when it did not report one (the caller estimates, never zero). */
  costUsd: number | null;
  inputTokens: number | null;
  latencyMs: number;
}

export interface JevEvidencePort {
  readonly model: string;
  judgeImplementation(input: JevEvidenceInput, signal: AbortSignal): Promise<JevEvidenceAnswer>;
}
